import { Suspense, useEffect } from "react";
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import AuthModal from "@/components/auth/AuthModal";
import ComingSoonPopup from "@/components/ComingSoonPopup";
import OnboardingGates from "@/components/auth/OnboardingGates";
import ScrollToTop from "./components/ScrollToTop";
import ProtectedRoute from "./components/ProtectedRoute";
import { PageLoader } from "./components/PageLoader";
import { useLanguageTracking } from "@/hooks/useLanguageTracking";
import { usePageTracking } from "@/hooks/usePageTracking";
import { initSentry, enableSentryReplayDeferred } from "@/lib/sentry";

// Index es estático: es la ruta raíz crítica, debe cargar sin delay
import Index from "./pages/Index";
import MobileBottomNav from "./components/MobileBottomNav";

// CookieBanner — diferido: no es contenido crítico (LCP) y su render inicial
// no debe competir con la pintura del above-the-fold en móvil.
const CookieBanner = lazy(() => import("./components/CookieBanner"));

// PwaInstallBanner — diferido: guía de instalación para Android (beforeinstallprompt)
// e iOS (aviso de Share). Se oculta automáticamente si la app ya está instalada
// (display-mode: standalone) o si el usuario descartó el aviso.
const PwaInstallBanner = lazy(() => import("./components/PwaInstallBanner"));

// Rutas públicas frecuentes — lazy: se cargan solo cuando el usuario navega a ellas
const Catalogo         = lazy(() => import("./pages/Comunidad"));
const SetDetail        = lazy(() => import("./pages/SetDetail"));
const SobreNosotros    = lazy(() => import("./pages/SobreNosotros"));
const Contacto         = lazy(() => import("./pages/Contacto"));
const Mensajes         = lazy(() => import("./pages/Mensajes"));
const Regalar          = lazy(() => import("./pages/Regalar"));

// Páginas de contenido / legales (poco visitadas) — lazy
const PrivacyPolicy    = lazy(() => import("./pages/PrivacyPolicy"));
const LegalNotice      = lazy(() => import("./pages/LegalNotice"));
const TerminosServicio = lazy(() => import("./pages/TerminosServicio"));
const ProteccionSets   = lazy(() => import("./pages/ProteccionSets"));
const CookiesPolicy    = lazy(() => import("./pages/CookiesPolicy"));
const FAQ              = lazy(() => import("./pages/FAQ"));
const Unsubscribe      = lazy(() => import("./pages/Unsubscribe"));
const Beta             = lazy(() => import("./pages/Beta"));
const NotFound         = lazy(() => import("./pages/NotFound"));

// Rutas protegidas (solo usuarios autenticados) — lazy: solo se cargan si el user hace login
const Dashboard   = lazy(() => import("./pages/Dashboard"));
const Admin       = lazy(() => import("./pages/Admin"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,        // 5 minutos de caché
      gcTime: 10 * 60 * 1000,          // 10 minutos garbage collection
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: 1,
    },
  },
});

const AppContent = () => {
  const {
    user,
    isLoading,
    isAdmin,
    isAuthModalOpen,
    openAuthModal,
    closeAuthModal,
    authModalMode,
    refreshProfile,
  } = useAuth();
  const location = useLocation();

  // Analytics globales:
  // - Sincroniza el idioma actual con GA4 (user_property "language")
  // - Emite page_view en cada cambio de ruta (SPA — no lo hace gtag por defecto)
  useLanguageTracking();
  usePageTracking();

  // Importar toast hook para mostrar mensajes de confirmación
  const { toast } = useToast();

  // ✅ Detectar y procesar tokens de confirmación de email en el hash fragment
  // Supabase redirige a la URL con #access_token=... cuando el usuario confirma su email
  useEffect(() => {
    // 1. Verificar si hay un hash fragment con tokens (retorno de OAuth o confirmación)
    const hash = window.location.hash;
    if (hash && hash.includes('access_token')) {
      // Limpiar el hash de la URL después de procesarlo
      window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
    }
    
    // 2. Detectar parámetro ?confirmed=email (backup para flujos que usen query params)
    const params = new URLSearchParams(location.search);
    if (params.get("confirmed") === "email") {
      // Limpiar el parámetro de la URL sin mostrar modal ni toast
      window.history.replaceState({}, document.title, location.pathname);
    }

    // 3. Detectar parámetro ?gift_code=BRICK-... (enlace de bono de regalo enviado por email)
    const giftCodeParam = params.get("gift_code");
    if (giftCodeParam && giftCodeParam.trim().length > 0) {
      const normalizedCode = giftCodeParam.trim().toUpperCase();
      localStorage.setItem("bs_pending_gift_code", normalizedCode);
      params.delete("gift_code");
      const newSearch = params.toString();
      window.history.replaceState(
        {},
        document.title,
        location.pathname + (newSearch ? `?${newSearch}` : ""),
      );

      if (!isLoading && !user) {
        toast({
          title: "🎁 ¡Tienes un Bono Regalo esperándote!",
          description: "Regístrate o inicia sesión para acreditar el saldo promocional en tu cuenta.",
        });
        openAuthModal("signup", "gift_voucher_link");
      }
    }
  }, [location.pathname, location.search, location.hash, isLoading, user, openAuthModal, toast]);

  // ✅ Canjear código de bono pendiente en localStorage tras login / registro
  useEffect(() => {
    if (!user?.id) return;
    const pendingGiftCode = localStorage.getItem("bs_pending_gift_code");
    if (!pendingGiftCode) return;

    localStorage.removeItem("bs_pending_gift_code");
    (async () => {
      try {
        const { data } = await supabase.rpc("redeem_gift_voucher_by_code", {
          p_code: pendingGiftCode,
        });
        const res = data as { success?: boolean; amount_cents?: number } | null;
        await refreshProfile();
        if (res?.success && res.amount_cents) {
          const eur = (res.amount_cents / 100).toFixed(2).replace(".", ",");
          toast({
            title: "🎁 ¡Bono Regalo acreditado!",
            description: `Se han sumado ${eur} € a tu saldo promocional Brickshare.`,
          });
        }
      } catch {
        // Ignorar errores silenciosos de auto-canje
      }
    })();
  }, [user?.id, refreshProfile, toast]);

  // 🚀 Initialize Sentry after first render (deferred to avoid blocking FCP/LCP)
  // Sentry will begin error tracking and performance monitoring, but Replay
  // will be enabled later via enableSentryReplayDeferred() after more idle time.
  useEffect(() => {
    // Use requestIdleCallback to initialize Sentry after the browser has finished
    // rendering the page. Falls back to setTimeout if requestIdleCallback is unavailable.
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        initSentry();
        // Enable Replay after another idle period to further reduce impact on FCP
        requestIdleCallback(() => {
          enableSentryReplayDeferred();
        });
      });
    } else {
      // Fallback for browsers without requestIdleCallback (older Safari, IE)
      setTimeout(() => {
        initSentry();
        setTimeout(() => {
          enableSentryReplayDeferred();
        }, 2000);
      }, 1000);
    }
  }, []);


  return (
    <>
      <ScrollToTop />
      {/* Suspense envuelve todas las rutas lazy — PageLoader muestra spinner durante la carga */}
      <div className="pb-16 md:pb-0 min-h-screen flex flex-col">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public routes - accessible to everyone */}
            <Route path="/" element={<Index />} />
            <Route path="/barcelona" element={<Navigate to="/" replace />} />
            <Route path="/alt" element={<Navigate to="/" replace />} />
            <Route path="/catalogo" element={<Catalogo />} />
            <Route path="/regalar" element={<Regalar />} />
            <Route path="/set/:refOrSlug" element={<SetDetail />} />
            <Route path="/catalogo/:refOrSlug" element={<SetDetail />} />
            <Route path="/mensajes" element={<ProtectedRoute><Mensajes /></ProtectedRoute>} />
            <Route path="/como-funciona" element={<Navigate to="/" replace />} />
            <Route path="/sobre-nosotros" element={<SobreNosotros />} />
            <Route path="/contacto" element={<Contacto />} />
            <Route path="/privacidad" element={<PrivacyPolicy />} />
            <Route path="/terminos" element={<Navigate to="/terminos-servicio" replace />} />
            <Route path="/terminos-condiciones" element={<Navigate to="/terminos-servicio" replace />} />
            <Route path="/terminos-y-condiciones" element={<Navigate to="/terminos-servicio" replace />} />
            <Route path="/terminos_servicio" element={<Navigate to="/terminos-servicio" replace />} />
            <Route path="/terminos-servicio" element={<TerminosServicio />} />
            <Route path="/proteccion-sets" element={<ProteccionSets />} />
            <Route path="/protege-set" element={<Navigate to="/proteccion-sets" replace />} />
            <Route path="/cookies" element={<CookiesPolicy />} />
            <Route path="/aviso-legal" element={<LegalNotice />} />
            <Route path="/preguntas-frecuentes" element={<FAQ />} />
            <Route path="/faq" element={<Navigate to="/preguntas-frecuentes" replace />} />
            <Route path="/faqs" element={<Navigate to="/preguntas-frecuentes" replace />} />
            <Route path="/faq's" element={<Navigate to="/preguntas-frecuentes" replace />} />

            {/* Baja de lista de espera (Coming Soon) — accesible sin auth */}
            <Route path="/unsubscribe" element={<Unsubscribe />} />

            {/* Acceso beta — URL no pública para testers (no aparece en nav ni sitemap) */}
            <Route path="/beta" element={<Beta />} />

            {/* Redirects para URLs con prefijo de idioma — evitan 404 si alguien llega desde bookmark o enlace externo */}
            <Route path="/es" element={<Navigate to="/" replace />} />
            <Route path="/en" element={<Navigate to="/" replace />} />
            <Route path="/ca" element={<Navigate to="/" replace />} />
            <Route path="/es/*" element={<Navigate to="/" replace />} />
            <Route path="/en/*" element={<Navigate to="/" replace />} />
            <Route path="/ca/*" element={<Navigate to="/" replace />} />

            {/* Protected routes - require authentication */}
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </div>
      <Suspense fallback={null}>
        <CookieBanner />
      </Suspense>
      <Suspense fallback={null}>
        <PwaInstallBanner />
      </Suspense>
      <AuthModal
        open={isAuthModalOpen}
        onOpenChange={(open) => !open && closeAuthModal()}
        initialMode={authModalMode}
      />
      {/* ❌ ComingSoonPopup ELIMINADO: web operativa en producción (22 oct 2026+) */}
      {/* <ComingSoonPopup /> */}
      {/* Gate de onboarding obligatorio para usuarios nuevos: Stripe Identity
          (solo documento) → mandato de tarjeta de crédito (SetupIntent off-session). */}
      <OnboardingGates />
      {/* Barra de navegación inferior táctil para smartphone */}
      <MobileBottomNav />
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;